import * as React from 'react';
import { FieldPath, UseFormReturn } from 'react-hook-form';

import { LoadRefPillInput } from 'components/input/LoadRefPillInput';
import { RHFCustomerSelect } from 'components/input/RHFCustomerSelect';
import { InputValue } from 'components/input/RHFTextInput';
import { Grid } from 'components/layout/Grid';
import { useExtendedFormContext } from 'contexts/extendedFormContext';
import { useServiceFeatures } from 'hooks/useServiceContext';
import { Load, NormalizedLoad, TMSCustomer } from 'types/Load';
import { Maybe } from 'types/UtilityTypes';
import { emailFieldOptions } from 'utils/formValidators';

import { LoadBuildingTextInput } from './UnifiedAljexLoadForm';
import { truncateCommodityDescription } from './characterLimits';

type CustomerSectionFormProps = {
  load?: Load;
  formMethods: UseFormReturn<NormalizedLoad>;
  customers: Maybe<TMSCustomer[]>;
  setCustomers: React.Dispatch<React.SetStateAction<Maybe<TMSCustomer[]>>>;
  isLoadingCustomers: boolean;
  setIsLoadingCustomers: React.Dispatch<React.SetStateAction<boolean>>;
  originalSuggestionData: NormalizedLoad;
  isCreateMode: boolean;
};

export default function CustomerSectionForm({
  load,
  formMethods,
  customers,
  setCustomers,
  isLoadingCustomers,
  setIsLoadingCustomers,
  isCreateMode,
  originalSuggestionData,
}: CustomerSectionFormProps): React.JSX.Element {
  const {
    control,
    formState: { errors },
    watch,
    setValue,
    getValues,
  } = formMethods;

  const { tmsIntegrations } = useServiceFeatures();
  const { aiDefaultValues } = useExtendedFormContext();
  const tmsID = isCreateMode ? tmsIntegrations?.[0]?.id : load?.tmsID;

  const watchedCustomer = watch('customer');
  const watchedAddress2 = watch('customer.addressLine2');

  const prevCustomerID = React.useRef(watchedCustomer?.externalTMSID);
  const commoditySetByCustomer = React.useRef(false);

  React.useEffect(() => {
    if (watchedCustomer?.externalTMSID) {
      const selectedCustomer = customers?.find(
        (c) => c.externalTMSID === watchedCustomer?.externalTMSID
      );
      if (!selectedCustomer) {
        return;
      }

      Object.entries(selectedCustomer).forEach(([key, value]) => {
        setValue(`customer.${key}` as FieldPath<NormalizedLoad>, value, {
          shouldDirty: aiDefaultValues,
        });
      });

      const hasCustomerChanged =
        watchedCustomer.externalTMSID !== prevCustomerID.current;

      if (selectedCustomer.defaultCommodity) {
        setValue(
          'specifications.commodities',
          truncateCommodityDescription(selectedCustomer.defaultCommodity),
          { shouldDirty: aiDefaultValues }
        );
        commoditySetByCustomer.current = true;
      } else if (hasCustomerChanged && commoditySetByCustomer.current) {
        // User switched to a customer without commodity, set to original AI suggestion if there is any
        setValue(
          'specifications.commodities',
          truncateCommodityDescription(
            originalSuggestionData?.specifications?.commodities || ''
          ),
          { shouldDirty: false }
        );
        commoditySetByCustomer.current = false;
      }
      prevCustomerID.current = watchedCustomer.externalTMSID;
    }
  }, [
    watchedCustomer?.externalTMSID,
    customers,
    setValue,
    aiDefaultValues,
    originalSuggestionData?.specifications?.commodities,
  ]);

  return (
    <>
      {isCreateMode ? (
        <RHFCustomerSelect
          required
          name={`customer.externalTMSID`}
          label='Name'
          control={control}
          errors={errors}
          customers={customers}
          setCustomers={setCustomers}
          isLoadingCustomers={isLoadingCustomers}
          setIsLoadingCustomers={setIsLoadingCustomers}
          tmsID={tmsID}
        />
      ) : (
        // Load.Customer.ExternalTMSID is not parsed in GetLoad, so we can't `injectSelectedObject` into RHFCustomerSelect
        // For now we simply show the name as read-only in edit mode as it's rarely changed after the load is created
        <LoadBuildingTextInput name='customer.name' label='Name' readOnly />
      )}

      <LoadBuildingTextInput
        name='customer.addressLine1'
        label='Address Line 1'
        readOnly
      />

      {!!watchedAddress2 && (
        <LoadBuildingTextInput
          name='customer.addressLine2'
          label='Address Line 2'
          readOnly
        />
      )}

      <Grid cols='4' gap='md' className='w-full m-0'>
        <div className='col-span-3'>
          <LoadBuildingTextInput name='customer.city' label='City' readOnly />
        </div>

        <div className='col-span-1'>
          <LoadBuildingTextInput name='customer.state' label='State' readOnly />
        </div>

        <div className='col-span-3'>
          <LoadBuildingTextInput
            name='customer.zipCode'
            label='ZIP'
            placeholder='12345'
            readOnly
          />
        </div>

        <div className='col-span-1'>
          <LoadBuildingTextInput
            name='customer.country'
            label='Country'
            readOnly
          />
        </div>
      </Grid>

      <LoadBuildingTextInput
        name='customer.contact'
        label='Contact'
        placeholder='Contact person'
        required={false}
      />

      <LoadBuildingTextInput
        name='customer.phone'
        label='Phone'
        placeholder='(555) 123-4567'
        required={false}
        inputValue={InputValue.PHONE_NUMBER}
      />

      {/* TODO: In Aljex integration, double-check we parse first non-empty email */}
      <LoadBuildingTextInput
        name='customer.email'
        label='Email'
        placeholder='contact@company.com'
        required={false}
        readOnly
        options={emailFieldOptions}
      />

      <LoadRefPillInput
        name='customer.refNumber'
        label='Customer Ref #'
        control={control}
        originalSuggestionData={originalSuggestionData || getValues()}
        autoSelectFirstOption={false}
      />
    </>
  );
}
